import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Capacitor } from '@capacitor/core'
import { getDayNumber, loadGameStats } from '../utils/puzzleUtils'
import { AboutModal } from '../components/AboutModal'
import { Footer } from '../components/Footer'

const BUG_REPORT_EMAIL = 'contactus@netherfire.com'

/**
 * 首页组件
 */
export function HomePage() {
  const { t } = useTranslation()
  const dayNumber = getDayNumber()
  const stats = loadGameStats()
  const [showAbout, setShowAbout] = useState(false)
  const [showHowToPlay, setShowHowToPlay] = useState(false)

  const handleReportBug = () => {
    const platform = Capacitor.getPlatform()
    const subject = encodeURIComponent(`[Blaze Grids] Bug Report (${platform})`)
    const body = encodeURIComponent(
      `Platform: ${platform}\nApp version: 1.0.0\n\nWhat happened:\n\n\nSteps to reproduce:\n`
    )
    window.location.href = `mailto:${BUG_REPORT_EMAIL}?subject=${subject}&body=${body}`
  }

  return (
    <div className="h-dvh overflow-hidden flex flex-col items-center justify-center text-white relative pt-safe pb-safe">
      {/* Main Content Container */}
      <div className="flex-1 min-h-0 flex flex-col items-center justify-center w-full max-w-lg px-4 text-center overflow-y-auto">

        {/* Logo 和标题 */}
        <div className="mb-6 mt-2">
          <img src="/logo-handpainted-512.png" alt="Blaze Grids Logo" className="w-20 h-20 mx-auto mb-4 animate-bounce-in" />
          <h1 className="text-3xl md:text-5xl font-bold mb-2 tracking-tight">
            <span className="text-brand-red">Blaze</span> Grids
          </h1>
          <p className="text-zinc-400 text-base">
            {t('app.subtitle')}
          </p>
        </div>

        {/* 主要操作按钮 */}
        <div className="w-full space-y-4">
          <Link
            to={`/daily?day=${dayNumber}`}
            className="block w-full btn btn-primary text-center text-lg py-4 flex items-center justify-center gap-3"
          >
            <span className="text-2xl">🔥</span>
            {t('home.play_daily', { number: dayNumber })}
          </Link>

          <Link
            to="/create"
            className="block w-full btn btn-secondary text-center text-lg py-4 flex items-center justify-center gap-3"
          >
            <span className="text-2xl">🛠️</span>
            {t('home.create_puzzle')}
          </Link>
        </div>

        {/* 统计信息 */}
        {stats.gamesPlayed > 0 && (
          <div className="mt-6 bg-zinc-900/80 border border-zinc-800 rounded-xl p-6 w-full backdrop-blur-sm">
            <h2 className="text-zinc-500 text-sm uppercase tracking-wider mb-4 font-bold">
              {t('home.stats')}
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-white font-mono">
                  {stats.gamesPlayed}
                </div>
                <div className="text-zinc-500 text-xs uppercase mt-1">{t('home.games_played')}</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-brand-orange font-mono">
                  {stats.currentStreak}
                </div>
                <div className="text-zinc-500 text-xs uppercase mt-1">{t('home.streak')}</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-brand-red font-mono">
                  {Math.round((stats.gamesWon / stats.gamesPlayed) * 100)}%
                </div>
                <div className="text-zinc-500 text-xs uppercase mt-1">{t('home.win_rate')}</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white font-mono">
                  {stats.bestTime === null ? '--' : `${Math.floor(stats.bestTime / 60)}:${(stats.bestTime % 60).toString().padStart(2, '0')}`}
                </div>
                <div className="text-zinc-500 text-xs uppercase mt-1">{t('home.best_time')}</div>
              </div>
            </div>
          </div>
        )}

        {/* 游戏说明入口 - 内容移进弹窗，首页保持单屏不滚动 */}
        <button
          onClick={() => setShowHowToPlay(true)}
          className="mt-6 text-zinc-500 hover:text-white underline transition-colors text-sm"
        >
          ❓ {t('help.title')}
        </button>
      </div>

      <Footer onAboutClick={() => setShowAbout(true)} />

      {/* 提交 Bug 入口 */}
      <button
        onClick={handleReportBug}
        className="fixed bottom-4 right-4 w-12 h-12 rounded-full bg-zinc-900/80 border border-zinc-700 backdrop-blur-sm flex items-center justify-center hover:bg-zinc-800 transition-colors z-40"
        aria-label={t('account.report_bug')}
        title={t('account.report_bug')}
      >
        <img src="/icon-bug-report-handpainted-128.png" alt="" className="w-7 h-7" />
      </button>

      {/* About Modal */}
      {showAbout && <AboutModal onClose={() => setShowAbout(false)} />}

      {/* How to Play Modal */}
      {showHowToPlay && (
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
          onClick={() => setShowHowToPlay(false)}
        >
          <div
            className="bg-gray-900 rounded-2xl p-6 max-w-md w-full max-h-[80vh] overflow-y-auto shadow-2xl border border-gray-700 text-left"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-zinc-100">{t('help.title')}</h2>
              <button
                onClick={() => setShowHowToPlay(false)}
                className="text-gray-500 hover:text-white transition-colors text-2xl leading-none"
                aria-label={t('game.close')}
              >
                ✕
              </button>
            </div>
            <div className="space-y-6 text-sm">
              <div>
                <h3 className="text-brand-red font-semibold mb-2">{t('help.objective')}</h3>
                <p className="text-zinc-400 leading-relaxed">
                  {t('help.objective_desc')}
                </p>
              </div>
              <div>
                <h3 className="text-brand-orange font-semibold mb-2">{t('help.tips_title')}</h3>
                <ul className="text-zinc-400 space-y-1 list-disc list-inside">
                  <li>{t('help.tip1')}</li>
                  <li>{t('help.tip2')}</li>
                  <li>{t('help.tip3')}</li>
                </ul>
              </div>
              <div>
                <h3 className="text-zinc-100 font-semibold mb-2">{t('help.ugc_title')}</h3>
                <p className="text-zinc-400 leading-relaxed">
                  {t('help.ugc_desc')}
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowHowToPlay(false)}
              className="mt-6 w-full btn btn-secondary"
            >
              {t('game.close')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
